/**
 * Clave guardada: se escribe enmascarada y no se muestra sola nunca.
 *
 * Lo estrenaron las claves de MIBA y ARCA de la ficha del administrador, que es
 * el unico caso del sistema donde se guarda la credencial de un tercero para
 * entrar a una plataforma externa. Antes de esto el registro de widgets no tenia
 * ningun `password`, y eso NO daba un error: `resolveWidgetName` degrada lo que
 * no conoce a `char` —avisando solo por consola y solo en DEV—, o sea que
 * declarar `widget="password"` sin este archivo habria dibujado la clave en un
 * input de texto comun, a la vista de cualquiera que pasara por atras.
 *
 * LAS DOS MITADES. Este componente cubre el campo EDITABLE. La otra mitad es la
 * rama `password` de `FieldDisplay`, y hace falta igual: sin ella el modo de
 * solo lectura y —peor— las CELDAS DE LA LISTA imprimirian la clave en texto
 * plano, porque `ReadonlyOutput` delega en `FieldDisplay` y lo que no tiene rama
 * propia cae al valor crudo. Entregar solo este archivo deja el formulario
 * prolijo y la pantalla de listado filtrando la clave.
 *
 * LO QUE ESTE WIDGET NO ES. No es seguridad: es discrecion en pantalla. Quien no
 * tiene que ver la clave no la tiene que RECIBIR, y de eso se ocupa el servidor
 * recortando la metadata por grupo (`FieldSpec.groups`, aplicado en
 * `core/api.py::_visible_fields`). Un widget nunca decide permisos —la regla
 * dura del contrato de esta carpeta—: recibe `readonly` ya resuelto y obedece.
 * Y de que la clave no viaje en la foto de demo se ocupa `limpiar()` del
 * exportador, que es otra defensa mas y tampoco esta.
 *
 * LA TERCERA MITAD, que aparecio despues: `EntradaDeClave`. La clave dejo de
 * ser sólo un campo de la ficha el dia que el desplegable «Que es este
 * contacto?» paso a pedir los accesos MIBA/ARCA adentro del formulario del
 * contacto, o sea como `ActionParam` y no como `FieldSpec`. Ese camino lo dibuja
 * `ActionParamControl`, que es otro archivo. Se extrajo la caja enmascarada en
 * vez de escribirla de nuevo alla: dos mascaras es una que en algun momento
 * arranca destapada, o que se olvida el `autoComplete="new-password"` y le
 * ofrece al navegador guardar la credencial de un tercero.
 */

import { useId, useState } from 'react';

import { controlClass } from '@/components/fields/controls';
import { ReadonlyOutput } from '@/components/fields/FieldDisplay';
import type { FieldInputProps } from '@/components/fields/types';

/**
 * La caja enmascarada con su boton de ver/ocultar. Una sola, para el campo de un
 * modelo y para el parametro de una accion.
 *
 * Recibe lo minimo a proposito: no sabe de `FieldMeta`, ni de `ActionParam`, ni
 * de permisos. Quien la usa ya resolvio todo eso.
 */
export function EntradaDeClave({
  id,
  name,
  value,
  onChange,
  required = false,
  invalid = false,
  describedBy,
  maxLength,
  ...resto
}: {
  id: string;
  name?: string;
  value: string;
  onChange: (valor: string) => void;
  required?: boolean;
  invalid?: boolean;
  describedBy?: string;
  maxLength?: number;
} & Record<`data-${string}`, unknown>) {
  const [visible, setVisible] = useState(false);
  const avisoId = useId();

  return (
    <div className="flex items-center gap-2">
      <input
        {...resto}
        id={id}
        {...(name === undefined ? {} : { name })}
        // Arranca SIEMPRE tapada: el estado por defecto es el seguro. El boton
        // de al lado la destapa a pedido, porque quien tiene permiso para verla
        // necesita poder leerla para usarla —si no, guardar una clave que no se
        // puede releer es un pozo—.
        type={visible ? 'text' : 'password'}
        className={controlClass(invalid)}
        value={value}
        required={required}
        aria-required={required || undefined}
        aria-invalid={invalid || undefined}
        aria-describedby={[describedBy, avisoId].filter(Boolean).join(' ') || undefined}
        {...(maxLength === undefined ? {} : { maxLength })}
        // `new-password` y no `off`: `off` lo ignoran casi todos los navegadores
        // en un campo de tipo password y ofrecen igual el gestor de claves. Esto
        // NO es el ingreso al sistema —es la credencial de un tercero anotada en
        // una ficha—, asi que ni se autocompleta con la del usuario ni se le
        // ofrece guardarla en el navegador.
        autoComplete="new-password"
        data-1p-ignore="true"
        data-lpignore="true"
        spellCheck={false}
        onChange={(evento) => {
          onChange(evento.target.value);
        }}
      />
      <button
        type="button"
        className="shrink-0 rounded-md border border-line px-2 py-1 text-xs text-fg-muted transition-colors hover:border-line-strong hover:text-fg"
        // El boton dice lo que VA A HACER, y ademas se anuncia con `aria-pressed`
        // el estado actual: un lector de pantalla tiene que poder saber si la
        // clave esta destapada sin tener que leerla.
        aria-pressed={visible}
        onClick={() => setVisible((antes) => !antes)}
      >
        {visible ? 'Ocultar' : 'Ver'}
      </button>
      <span id={avisoId} className="sr-only">
        {visible ? 'La clave esta visible en pantalla.' : 'La clave esta oculta.'}
      </span>
    </div>
  );
}

export function PasswordField({
  id,
  meta,
  value,
  onChange,
  readonly,
  required,
  invalid,
  describedBy,
}: FieldInputProps) {
  if (readonly) {
    return <ReadonlyOutput id={id} meta={meta} value={value} {...(describedBy === undefined ? {} : { describedBy })} />;
  }

  const texto =
    typeof value === 'string' ? value : value === null || value === undefined ? '' : String(value);

  return (
    <EntradaDeClave
      id={id}
      value={texto}
      required={required}
      invalid={invalid}
      {...(describedBy === undefined ? {} : { describedBy })}
      {...(meta.max_length === undefined ? {} : { maxLength: meta.max_length })}
      onChange={(nuevo) => {
        // Un campo de modelo que admite nulos guarda `null` y no `""` cuando se
        // vacia: es la diferencia entre «no tiene clave» y «tiene la clave
        // vacia», y la sabe el campo, no la caja.
        onChange(nuevo === '' && meta.null === true ? null : nuevo);
      }}
    />
  );
}
